import { useMemo, useState } from 'react'

import { useQuery } from '@apollo/client'
import { FoldableEllipsis } from '@place/ui'
import { Dayjs } from 'dayjs'

import { CommonShare3Icon } from '@local/assets/icons'
import { extractByTypename } from '@local/utils'

import SocialPlugin from '@web/components/SocialPlugin'
import { gql } from '@web/gql'
import useFollowMethods from '@web/hooks/useFollowMethods'
import { useNavigation } from '@web/hooks/useNavigation'
import useNow from '@web/hooks/useNow'
import usePageInfo from '@web/hooks/usePageInfo'
import { DEFAULT_USER_NICKNAME } from '@web/libs/constants'
import links from '@web/libs/constants/links'
import routes from '@web/libs/constants/routes'
import { extractErrorMessage, neloLogger } from '@web/libs/utils'
import {
  MissionCard as MissionCardHelper,
  MissionCardProps,
} from '@web/pages/mission/components/MissionCard/MissionCard'
import { missionGroupsToMissionCards } from '@web/pages/mission/components/MissionCard/MissionCard.transform'
import ActionBar from '@web/pages/review/components/Header/ActionBar'
import UIProfile from '@web/ui/pages/review/components/UIProfile'
import styles from '@web/ui/pages/review/components/UIProfile/Profile.module.scss'
import UIAcceptFollowNudge from '@web/ui/pages/review/components/UIProfile/UIAcceptFollowNudge'
import UICompleteFollowNudge from '@web/ui/pages/review/components/UIProfile/UICompleteFollowNudge'

export const ReviewProfileMissionGroupsQuery = gql(`
  query ReviewProfileMissionGroups($yearMonth: YearMonth!) {
    missionGroups(yearMonth: $yearMonth, sort: WEIGHT_FIRST) {
      ... on MissionGroupsSucceed {
        missionGroups {
          ...MissionGroupsToMissionCards_MissionGroup
        }
      }
    }
  }
`)

export const useMissionTitleAndDays = (today: Dayjs, missionCards?: MissionCardProps[]) => {
  const thisMonth = today.format('M')

  const missionStartDate = missionCards?.[0]?.startDateTime || today.startOf('month')
  const missionEndDate = today.endOf('month')
  const leftDaysOfThisMonth = missionEndDate.diff(today, 'day')
  const daysAfterMissionStart = today.diff(missionStartDate, 'day')

  // 스탬프 최소 한개 이상 받았어야 참여
  const isParticipating = missionCards?.some(missionCard => missionCard.stampGroups.length > 0)

  if (daysAfterMissionStart >= 0 && daysAfterMissionStart < 8) {
    return { title: `${thisMonth}월 미션 오픈!` }
  }

  if (leftDaysOfThisMonth < 8) {
    return { title: `${thisMonth}월 미션 종료 임박!`, leftDays: leftDaysOfThisMonth }
  }

  if (isParticipating) {
    return { title: `${thisMonth}월 미션 진행 중!` }
  }

  return { title: `${thisMonth}월 미션 참여하기!` }
}

const ReviewProfile = () => {
  const [isFolded, setIsFolded] = useState(true)
  const today = useNow()
  const { data } = useQuery(ReviewProfileMissionGroupsQuery, {
    variables: { yearMonth: today.format('YYYY-MM') },
    onError: error => {
      neloLogger('error', `[리뷰페이지] Mission 조회 실패, ${extractErrorMessage(error)}`)
    },
  })

  const { MissionGroupsSucceed } = extractByTypename(data?.missionGroups)

  const missionCards = useMemo(
    () =>
      MissionCardHelper.sortCardsByLatestAndWeight(
        missionGroupsToMissionCards(MissionGroupsSucceed?.missionGroups ?? []),
      ),
    [MissionGroupsSucceed],
  )

  const { title, leftDays } = useMissionTitleAndDays(today, missionCards)

  const { isMyPage, pageOwner } = usePageInfo()
  const navigation = useNavigation()

  const handleClickOthersProfileImage = () => {
    if (!pageOwner?.profile) return

    const profileImageUrl = pageOwner.profile.originImageUrl ?? pageOwner.profile.imageUrl

    if (!profileImageUrl) return

    navigation.push({ url: profileImageUrl, openWindow: true })
  }

  const handleClickProfileImage = () => {
    if (isMyPage) navigation.push({ url: routes.SETTING_PROFILE })
    else handleClickOthersProfileImage()
  }

  const checkIsValidClick = () => {
    if (!pageOwner) {
      navigation.goToLogin()
      return false
    }
    return true
  }

  const shareUrl = pageOwner ? `${links.MY_HOME}/${pageOwner.id}/review` : ''

  const SocialPluginComponent = (
    <span className={styles.btn_wrapper}>
      <SocialPlugin
        className={styles.btn_sub}
        url={shareUrl}
        isValidClick={checkIsValidClick}
        // clickCode={nClicksArea.SHARE}
      >
        <span className="place_blind">공유하기</span>
        <CommonShare3Icon className={styles.icon} aria-hidden="true" />
      </SocialPlugin>
    </span>
  )

  const onToggleButtonClick = () => {
    setIsFolded(!isFolded)
  }

  const introduction = pageOwner?.profile.introduction
  const FoldableEllipsisComponent = introduction ? (
    <FoldableEllipsis
      text={introduction}
      displayLines={2}
      isFolded={isFolded}
      showToggleButton={true}
      onClickText={onToggleButtonClick}
      onToggleButtonClick={onToggleButtonClick}
    />
  ) : null

  const handleClickReview = () =>
    pageOwner?.id && navigation.push({ url: routes.HOME_REVIEW, params: { userId: pageOwner?.id } })

  const handleClickFollower = () =>
    pageOwner?.id && navigation.push({ url: routes.FOLLOWERS, params: { userId: pageOwner?.id } })

  const handleClickFollowing = () =>
    pageOwner?.id && navigation.push({ url: routes.FOLLOWING, params: { userId: pageOwner?.id } })

  const handleClickMission = () => navigation.push({ url: routes.MISSION })

  const { acceptFollowRequest } = useFollowMethods()

  const isFollowRequestedUser = !isMyPage && pageOwner?.isFollowRequester
  const displayNickname = pageOwner?.profile.nickname || DEFAULT_USER_NICKNAME
  const [showCompleteNudge, setShowCompleteNudge] = useState(false)
  const handleAcceptFollowRequest = () => {
    if (!pageOwner) return

    acceptFollowRequest(pageOwner, () => setShowCompleteNudge(true))
  }

  const handleManageFollowClick = () => {
    navigation.push({ url: routes.FOLLOWERS_NO_LOGIN })
  }

  const top =
    isFollowRequestedUser && !showCompleteNudge ? (
      <UIAcceptFollowNudge displayNickname={displayNickname} onAccept={handleAcceptFollowRequest} />
    ) : showCompleteNudge ? (
      <UICompleteFollowNudge displayNickname={displayNickname} onManageClick={handleManageFollowClick} />
    ) : null

  return (
    <UIProfile
      top={top}
      login={!!pageOwner}
      isMyPage={isMyPage}
      isFollowing={pageOwner?.isFriend}
      nickname={displayNickname}
      profileImageUrl={pageOwner?.profile.imageUrl || undefined}
      borderImageUrl={pageOwner?.profile.border?.imageUrl || undefined}
      onClickProfile={handleClickProfileImage}
      onClickReview={handleClickReview}
      onClickFollower={handleClickFollower}
      onClickFollowing={handleClickFollowing}
      onClickMission={handleClickMission}
      reviewCount={pageOwner?.stat.reviewCount}
      followingCount={pageOwner?.stat.followingCount}
      followerCount={pageOwner?.stat.followerCount}
      foldableEllipsis={FoldableEllipsisComponent}
      missionTitle={title}
      missionLeftDays={leftDays}
      socialPlugin={isMyPage && shareUrl ? SocialPluginComponent : null}
      actionBar={<ActionBar />}
      isOfficial={pageOwner?.isThemeContentProvider}
    />
  )
}

export default ReviewProfile
